// Queries of the Assessments area: the assessments of a region (one cached list shared by the page, the Overview card and the sidebar badge, polled while a run is queued or running), the capability documents (polled while one is being read), a followed job (its detail, then live server-sent events while it is active, polling as the fallback), an assessment's run job with the time it was seen ended or gone (the list is refreshed then) and the reason a document was stored without being read, remembered for this session.
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import type { AssessmentSummary, CapabilityDocumentSummary, OperationJob } from "@/api/__generated__";
import {
  assessmentsListAssessmentsOptions,
  assessmentsListAssessmentsQueryKey,
  capabilitiesListCapabilityDocumentsOptions,
  jobsGetJobOptions,
} from "@/api/__generated__/@tanstack/react-query.gen";
import type { ProblemCopy } from "@/api/problem";
import { isActiveJob } from "@/features/jobs/labels";
import { useJobEvents } from "@/features/jobs/useJobEvents";

import { isRunning, jobGone, runJobEnded } from "./labels";

export const RUNNING_POLL_MS = 10_000;
export const READING_POLL_MS = 5_000;
export const JOB_POLL_MS = 15_000;
export const BADGE_POLL_MS = 60_000;

function pollWhile<T>(running: (items: readonly T[]) => boolean, ms: number) {
  return (query: { state: { data: readonly T[] | undefined } }) =>
    query.state.data !== undefined && running(query.state.data) ? ms : false;
}

export function assessmentsQueryOptions(jurisdiction: string) {
  return {
    ...assessmentsListAssessmentsOptions({ query: { jurisdiction } }),
    staleTime: 30_000,
    refetchInterval: pollWhile<AssessmentSummary>((items) => items.some(isRunning), RUNNING_POLL_MS),
  };
}

export function isReading(document: Pick<CapabilityDocumentSummary, "status">): boolean {
  return document.status === "extracting";
}

export function capabilityDocumentsQueryOptions() {
  return {
    ...capabilitiesListCapabilityDocumentsOptions(),
    staleTime: 30_000,
    refetchInterval: pollWhile<CapabilityDocumentSummary>((items) => items.some(isReading), READING_POLL_MS),
  };
}

function useFollowedJobQuery(jobId: string | null) {
  const job = useQuery({
    ...jobsGetJobOptions({ path: { job_id: jobId ?? "" } }),
    enabled: jobId !== null,
    refetchInterval: (query) =>
      query.state.data !== undefined && isActiveJob(query.state.data) ? JOB_POLL_MS : false,
  });
  const active = job.data !== undefined && isActiveJob(job.data);
  useJobEvents(jobId ?? "", jobId !== null && active);
  return job;
}

export function useFollowedJob(jobId: string | null): OperationJob | undefined {
  const job = useFollowedJobQuery(jobId);
  return jobId === null ? undefined : job.data;
}

export interface RunJob {
  job: OperationJob | undefined;
  endedAt: number | null;
}

export function useRunJob(jobId: string | null): RunJob {
  const queryClient = useQueryClient();
  const query = useFollowedJobQuery(jobId);
  const gone = jobId !== null && jobGone(query.error);
  const job = jobId === null ? undefined : query.data;
  const endedAt = gone ? query.errorUpdatedAt : runJobEnded(job) ? query.dataUpdatedAt : null;
  const ended = endedAt !== null;
  useEffect(() => {
    if (ended) {
      void queryClient.invalidateQueries({ queryKey: assessmentsListAssessmentsQueryKey() });
    }
  }, [ended, queryClient]);
  return { job, endedAt };
}

function readingProblemKey(capabilityDocUid: string) {
  return ["assessments", "reading-problem", capabilityDocUid] as const;
}

export function rememberReadingProblem(
  queryClient: QueryClient,
  capabilityDocUid: string,
  copy: ProblemCopy | null,
): void {
  queryClient.setQueryData(readingProblemKey(capabilityDocUid), copy);
}

export function useReadingProblem(capabilityDocUid: string): ProblemCopy | null {
  const queryClient = useQueryClient();
  const remembered = useQuery({
    queryKey: readingProblemKey(capabilityDocUid),
    queryFn: () => queryClient.getQueryData<ProblemCopy | null>(readingProblemKey(capabilityDocUid)) ?? null,
    enabled: false,
    staleTime: Infinity,
  });
  return remembered.data ?? null;
}
